/**
 * KeyboardShortcuts Component
 *
 * Displays a modal with all available keyboard shortcuts in the app.
 */

import { CSSProperties } from 'react';

interface KeyboardShortcutsProps {
  isVisible: boolean;
  onClose: () => void;
}

interface Shortcut {
  keys: string;
  description: string;
}

interface ShortcutSection {
  title: string;
  shortcuts: Shortcut[];
}

const SHORTCUTS: ShortcutSection[] = [
  {
    title: 'Playback',
    shortcuts: [
      { keys: 'Space', description: 'Play/Pause' },
      { keys: 'J', description: 'Play reverse (press multiple times for 2x, 3x, 4x speed)' },
      { keys: 'K', description: 'Stop' },
      { keys: 'L', description: 'Play forward (press multiple times for 2x, 3x, 4x speed)' },
      { keys: 'Home', description: 'Jump to start' },
      { keys: 'End', description: 'Jump to end' },
      // { keys: '←/→', description: 'Skip backward/forward' },
    ],
  },
  {
    title: 'Node Editing',
    shortcuts: [
      { keys: 'B', description: 'Create new beat after current beat and focus script input' },
      { keys: 'Shift+Drag', description: 'Multi-select nodes (box)' },
      { keys: 'Shift+Click', description: 'Toggle node selection' },
      { keys: 'Ctrl+C', description: 'Copy selected node(s)' },
      { keys: 'Ctrl+V', description: 'Paste at last clicked position' },
      { keys: 'Delete', description: 'Delete selected node(s)' },
      { keys: 'Backspace', description: 'Delete selected node(s)' },
      { keys: 'Ctrl+Z', description: 'Undo' },
      { keys: 'Ctrl+Shift+Z', description: 'Redo' },
      { keys: 'C', description: 'Comment Box' },
    ],
  },
  {
    title: 'Script Window',
    shortcuts: [
      { keys: 'Click', description: 'Edit beat title, scene heading, or script' },
      { keys: 'Double-Click', description: 'Show scene heading presets (in scene heading field)' },
      { keys: '↑/↓', description: 'Navigate scene heading suggestions' },
      { keys: 'Enter', description: 'Select suggestion or finish editing' },
      { keys: 'Escape', description: 'Close suggestions or cancel editing' },
      { keys: 'Tab', description: 'Insert tab (in script)' },
    ],
  },
  {
    title: 'Reference Library',
    shortcuts: [
      { keys: 'N', description: 'Create note node at center' },
      { keys: 'Double-Click', description: 'Create empty reference' },
      { keys: 'Ctrl+V', description: 'Paste image as reference' },
      { keys: 'Alt+Drag', description: 'Duplicate reference or note' },
    ],
  },
  {
    title: 'Help',
    shortcuts: [
      { keys: 'Escape', description: 'Close dialogs' },
    ],
  },
  {
    title: 'View',
    shortcuts: [
      { keys: 'Ctrl+Scroll', description: 'Zoom in/out (Node View)' },
      { keys: 'Middle Mouse', description: 'Pan (Node View)' },
      { keys: 'F', description: 'Frame currently playing node' },
      { keys: 'Shift+F', description: 'Frame currently selected node' },
    ],
  },
];

function KeyboardShortcuts({ isVisible, onClose }: KeyboardShortcutsProps) {
  if (!isVisible) return null;

  const overlayStyles: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  };

  const modalStyles: CSSProperties = {
    backgroundColor: 'var(--nb-bg-primary)',
    border: '2px solid var(--nb-accent-primary)',
    borderRadius: 'var(--nb-radius-md)',
    padding: 'var(--nb-space-lg)',
    minWidth: '480px',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  };

  const headerStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'var(--nb-space-md)',
    paddingBottom: 'var(--nb-space-sm)',
    borderBottom: '1px solid var(--nb-border-primary)',
  };

  const titleStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-lg)',
    fontWeight: 600,
    color: 'var(--nb-text-primary)',
  };

  const closeButtonStyles: CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'var(--nb-text-secondary)',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 var(--nb-space-xs)',
    lineHeight: 1,
  };

  const sectionStyles: CSSProperties = {
    marginBottom: 'var(--nb-space-md)',
  };

  const sectionTitleStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: 600,
    color: 'var(--nb-accent-primary)',
    marginBottom: 'var(--nb-space-xs)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const shortcutRowStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--nb-space-xs) 0',
  };

  const keysStyles: CSSProperties = {
    fontFamily: 'var(--nb-font-mono)',
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
    backgroundColor: 'var(--nb-bg-secondary)',
    padding: '2px 8px',
    borderRadius: 'var(--nb-radius-sm)',
    border: '1px solid var(--nb-border-primary)',
  };

  const descriptionStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-secondary)',
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={overlayStyles} onClick={handleOverlayClick}>
      <div style={modalStyles}>
        <div style={headerStyles}>
          <h2 style={titleStyles}>Keyboard Shortcuts</h2>
          <button
            style={closeButtonStyles}
            onClick={onClose}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--nb-text-primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--nb-text-secondary)')}
            title="Close (Escape)"
          >
            ×
          </button>
        </div>

        {SHORTCUTS.map((section, sectionIndex) => (
          <div key={sectionIndex} style={sectionStyles}>
            <div style={sectionTitleStyles}>{section.title}</div>
            {section.shortcuts.map((shortcut, shortcutIndex) => (
              <div key={shortcutIndex} style={shortcutRowStyles}>
                <span style={keysStyles}>{shortcut.keys}</span>
                <span style={descriptionStyles}>{shortcut.description}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default KeyboardShortcuts;
